import React from "react";

/**
 * Form primitives for the billing modals.
 *
 * These exist because the modals were mixing two unrelated control styles: the
 * shared `Textfield` (48px tall, 16px radius, grey fill) for text inputs and a
 * bare `.custom-credit-select` (40px tall, 8px radius, near-white fill) for
 * dropdowns, with labels rendered by two different code paths. Side by side in a
 * two-column grid the mismatch was the main thing making the form look unfinished.
 *
 * One control height (44px), one radius, one label treatment, one error slot.
 */

const CONTROL_BASE =
  "h-11 w-full rounded-xl border bg-white px-3 font-['Geist:Regular',_sans-serif] text-[14px] leading-[20px] text-[#211f26] outline-none transition-colors placeholder:text-[#a7a3ad] focus:border-[#2d58ff] focus:ring-2 focus:ring-[#2d58ff]/15 disabled:cursor-not-allowed disabled:bg-[#f5f3f6] disabled:text-[#84828e]";

const borderFor = (hasError?: boolean) =>
  hasError ? "border-[#d92d20]" : "border-[#eae7ec]";

export function SectionHeading({
  title,
  hint,
}: {
  title: string;
  hint?: React.ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-row items-center justify-between gap-3">
      <h3 className="font-['Geist:Medium',_sans-serif] text-[11px] font-medium uppercase leading-[16px] tracking-[0.08em] text-[#84828e]">
        {title}
      </h3>
      {hint && (
        <span className="font-['Geist:Regular',_sans-serif] text-[12px] leading-[16px] text-[#84828e]">
          {hint}
        </span>
      )}
    </div>
  );
}

/** Card that groups a set of related fields. */
export function FieldGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[#eae7ec] bg-[rgba(253,252,253,0.72)] p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export function Field({
  label,
  required,
  hint,
  error,
  help,
  fullWidth,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string;
  help?: React.ReactNode;
  fullWidth?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <div className="mb-1.5 flex flex-row items-baseline justify-between gap-2">
        <label className="font-['Geist:Medium',_sans-serif] text-[13px] font-medium leading-[18px] text-[#211f26]">
          {label}
          {required && <span className="ml-1 text-[#d92d20]">*</span>}
        </label>
        {hint && (
          <span className="shrink-0 font-['Geist:Regular',_sans-serif] text-[12px] leading-[16px] text-[#84828e]">
            {hint}
          </span>
        )}
      </div>
      {children}
      {error ? (
        <div className="mt-1.5 font-['Geist:Regular',_sans-serif] text-[12px] leading-[16px] text-[#d92d20]">
          {error}
        </div>
      ) : (
        help && (
          <div className="mt-1.5 font-['Geist:Regular',_sans-serif] text-[12px] leading-[16px] text-[#84828e]">
            {help}
          </div>
        )
      )}
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  error,
  prefix,
  inputMode,
  maxLength,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  /** Currency symbol or unit rendered inside the control. */
  prefix?: string;
  inputMode?: "text" | "decimal" | "numeric";
  maxLength?: number;
}) {
  if (prefix) {
    return (
      <div
        className={`flex h-11 w-full flex-row items-center rounded-xl border ${borderFor(
          Boolean(error),
        )} bg-white pl-3 transition-colors focus-within:border-[#2d58ff] focus-within:ring-2 focus-within:ring-[#2d58ff]/15`}
      >
        <span className="pointer-events-none flex h-5 shrink-0 items-center border-r border-[#eae7ec] pr-2.5 font-['Geist:Medium',_sans-serif] text-[13px] text-[#84828e]">
          {prefix}
        </span>
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          className="h-full w-full bg-transparent px-2 font-['Geist:Regular',_sans-serif] text-[14px] leading-[20px] text-[#211f26] outline-none placeholder:text-[#a7a3ad]"
        />
      </div>
    );
  }

  return (
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      maxLength={maxLength}
      className={`${CONTROL_BASE} ${borderFor(Boolean(error))}`}
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  error,
  rows = 3,
  maxLength,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  rows?: number;
  maxLength?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={`w-full resize-y rounded-xl border ${borderFor(
        Boolean(error),
      )} bg-white px-3 py-2.5 font-['Geist:Regular',_sans-serif] text-[14px] leading-[20px] text-[#211f26] outline-none transition-colors placeholder:text-[#a7a3ad] focus:border-[#2d58ff] focus:ring-2 focus:ring-[#2d58ff]/15`}
    />
  );
}

export function SelectInput({
  value,
  onChange,
  options,
  error,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  error?: string;
  disabled?: boolean;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        // Native arrow removed so the control matches the text inputs; the
        // chevron below is drawn at the same optical weight as the table icons.
        className={`${CONTROL_BASE} ${borderFor(
          Boolean(error),
        )} cursor-pointer appearance-none pr-10`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M6 9L12 15L18 9"
          stroke={disabled ? "#a7a3ad" : "#605d66"}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  );
}

/**
 * Small select for section headers. Same visual language as the pills in the
 * billing tables rather than a shrunken form control.
 */
export function CompactSelect({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel?: string;
}) {
  return (
    <div className="relative shrink-0">
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={ariaLabel}
        className="h-8 cursor-pointer appearance-none rounded-full border border-[#d6e4ff] bg-[#eff4ff] pl-3 pr-7 font-['Geist:Medium',_sans-serif] text-[12px] font-medium leading-[16px] text-[#2d58ff] outline-none"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          d="M6 9L12 15L18 9"
          stroke="#2d58ff"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
}

/** Read-only key/value line used by the modal footers to restate the change. */
export function SummaryLine({
  items,
}: {
  items: { label: string; value: React.ReactNode }[];
}) {
  return (
    <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
      {items.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 && <span className="text-[#d7d3dc]">·</span>}
          <span className="font-['Geist:Regular',_sans-serif] text-[13px] leading-[18px] text-[#84828e]">
            {item.label}{" "}
            <span className="font-['Geist:Medium',_sans-serif] font-medium text-[#211f26]">
              {item.value}
            </span>
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-11 items-center justify-center rounded-2xl bg-[#2d58ff] px-5 font-['Geist:Medium',_sans-serif] text-[14px] font-medium leading-[18px] text-white shadow-[0px_1px_1px_0px_inset_rgba(255,255,255,0.16)] transition-colors hover:bg-[#2449db] disabled:cursor-not-allowed disabled:bg-[#b9c6ff]"
    >
      {children}
    </button>
  );
}

export function SecondaryButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-11 items-center justify-center rounded-2xl border border-[#eae7ec] bg-[#f2eff3] px-5 font-['Geist:Medium',_sans-serif] text-[14px] font-medium leading-[18px] text-[#211f26] transition-colors hover:bg-[#e8e5ea]"
    >
      {children}
    </button>
  );
}
